import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Plus, Trash2, Upload, X, Search } from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { useStoreValidation } from '../../../hooks/useStoreValidation'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'

interface ProductVariant {
  id?: string
  variantName: string
  variantType: 'flavor' | 'color' | 'size' | 'other'
  stockQuantity: number
  sku?: string
  imageUrl?: string
  imageFile?: File
  imagePreview?: string
}

interface Category {
  id: string
  name: string
  slug: string
}

const EditProduct = () => {
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>()
  const navigate = useNavigate()
  const { activeStore } = useStore()
  const { requireApprovedStore } = useStoreValidation()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    stock_quantity: '',
    unit: 'unidad',
    is_available: true,
    is_featured: false,
    barcode: ''
  })

  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [selectedImageKey, setSelectedImageKey] = useState<string | null>(null)
  const [variants, setVariants] = useState<ProductVariant[]>([])
  const [showVariantsSection, setShowVariantsSection] = useState(false)
  const [showImageSelector, setShowImageSelector] = useState(false)
  const [availableImages, setAvailableImages] = useState<Array<{ key: string; signedUrl: string; size: number }>>([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [imageSearchTerm, setImageSearchTerm] = useState('')
  const [variantImageSelectorIndex, setVariantImageSelectorIndex] = useState<number | null>(null)

  useEffect(() => {
    if (productId && activeStore) {
      loadProduct()
      loadCategories()
    }
  }, [productId, activeStore])

  const loadProduct = async () => {
    if (!productId || !activeStore) return

    const cacheKey = `edit_product_${productId}`
    
    // Intentar cargar desde cache primero
    const cachedProduct = getCacheStale<any>(cacheKey)
    if (cachedProduct) {
      // Usar datos del cache para pre-llenar el formulario
      setFormData({
        name: cachedProduct.name || '',
        description: cachedProduct.description || '',
        price: cachedProduct.price?.toString() || '',
        category_id: cachedProduct.category_id || '',
        stock_quantity: cachedProduct.stock_quantity?.toString() || '',
        unit: cachedProduct.unit || 'unidad',
        is_available: cachedProduct.is_available !== false,
        is_featured: cachedProduct.is_featured || false,
        barcode: cachedProduct.barcode || ''
      })
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/products/${productId}`)
      
      if (response.data.success && response.data.data) {
        const product = response.data.data
        setFormData({
          name: product.name || '',
          description: product.description || '',
          price: product.price?.toString() || '',
          category_id: product.category_id || '',
          stock_quantity: product.stock_quantity?.toString() || '',
          unit: product.unit || 'unidad',
          is_available: product.is_available !== false,
          is_featured: product.is_featured || false,
          barcode: product.barcode || ''
        })
        
        // Cargar URL firmada para la imagen del producto principal
        if (product.image_url) {
          try {
            const signedUrlResponse = await api.get(`/upload/signed-url/${encodeURIComponent(product.image_url)}`)
            if (signedUrlResponse.data.success && signedUrlResponse.data.data?.signedUrl) {
              setImagePreview(signedUrlResponse.data.data.signedUrl)
            } else {
              setImagePreview('')
            }
          } catch (error) {
            console.error('Error loading product image preview:', error)
            setImagePreview('')
          }
        } else {
          setImagePreview('')
        }

        // Cargar variantes
        try {
          const variantsResponse = await api.get(`/products/${productId}/variants`)
          if (variantsResponse.data.success && variantsResponse.data.data) {
            const variantsWithPreviews = await Promise.all(
              variantsResponse.data.data.map(async (v: any) => {
                let imagePreview = ''
                if (v.image_url) {
                  try {
                    const signedUrlResponse = await api.get(`/upload/signed-url/${encodeURIComponent(v.image_url)}`)
                    if (signedUrlResponse.data.success && signedUrlResponse.data.data?.signedUrl) {
                      imagePreview = signedUrlResponse.data.data.signedUrl
                    }
                  } catch (error) {
                    console.error('Error loading variant image preview:', error)
                  }
                }
                
                return {
                  id: v.id,
                  variantName: v.variant_name,
                  variantType: v.variant_type,
                  stockQuantity: v.stock_quantity,
                  sku: v.sku || '',
                  imageUrl: v.image_url || '',
                  imagePreview: imagePreview,
                  imageFile: undefined
                }
              })
            )
            setVariants(variantsWithPreviews)
            setShowVariantsSection(variantsWithPreviews.length > 0)
          }
        } catch (error) {
          console.error('Error loading variants:', error)
          setVariants([])
          setShowVariantsSection(false)
        }
      }
    } catch (error: any) {
      console.error('Error loading product:', error)
      toast.error('Error al cargar el producto')
      navigate(`/store/${activeStore.id}/products`)
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    if (!activeStore) return

    const cacheKey = `edit_product_categories_${activeStore.id}`
    
    // Intentar cargar desde cache primero
    const cachedCategories = getCacheStale<Category[]>(cacheKey)
    if (cachedCategories && cachedCategories.length > 0) {
      setCategories(cachedCategories)
    }

    try {
      const response = await api.get(`/products/store/${activeStore.id}/categories`)
      if (response.data.success) {
        const categoriesData = response.data.data || []
        setCategories(categoriesData)
        
        // Guardar en cache (1 hora de TTL - las categorías cambian poco)
        setCache(cacheKey, categoriesData, 60 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading categories:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedCategories) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    }
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      setSelectedImageKey(null) // Limpiar imagen existente seleccionada
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSelectExistingImage = (imageKey: string, signedUrl: string) => {
    setSelectedImage(null) // Limpiar archivo seleccionado
    setSelectedImageKey(imageKey) // Guardar la key de la imagen seleccionada
    setImagePreview(signedUrl)
    setShowImageSelector(false)
    toast.success('Imagen seleccionada')
  }

  useEffect(() => {
    if (showImageSelector) {
      const loadImages = async () => {
        try {
          setLoadingImages(true)
          const response = await api.listImages('products', 100, imageSearchTerm || undefined)
          if (response.success && response.data) {
            setAvailableImages(response.data.filter((img: any) => img.signedUrl))
          }
        } catch (error) {
          console.error('Error loading images:', error)
          toast.error('Error al cargar imágenes disponibles')
        } finally {
          setLoadingImages(false)
        }
      }
      loadImages()
    } else {
      // Limpiar imágenes cuando se cierra el modal
      setAvailableImages([])
      setImageSearchTerm('')
      setVariantImageSelectorIndex(null)
    }
  }, [showImageSelector, imageSearchTerm])

  const handleVariantImageSelect = (index: number, file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const updated = [...variants]
      updated[index] = {
        ...updated[index],
        imageFile: file,
        imageUrl: '', // Limpiar imagen existente seleccionada
        imagePreview: reader.result as string
      }
      setVariants(updated)
    }
    reader.readAsDataURL(file)
  }

  const handleSelectExistingVariantImage = (imageKey: string, signedUrl: string) => {
    if (variantImageSelectorIndex !== null) {
      const updated = [...variants]
      updated[variantImageSelectorIndex] = {
        ...updated[variantImageSelectorIndex],
        imageFile: undefined, // Limpiar archivo seleccionado
        imageUrl: imageKey, // Guardar la key de la imagen seleccionada
        imagePreview: signedUrl
      }
      setVariants(updated)
      setVariantImageSelectorIndex(null)
      setShowImageSelector(false)
      toast.success('Imagen seleccionada para la variante')
    }
  }

  const addVariant = () => {
    setVariants([...variants, {
      variantName: '',
      variantType: 'flavor',
      stockQuantity: 0,
      sku: '',
      imageUrl: '',
      imageFile: undefined,
      imagePreview: ''
    }])
    setShowVariantsSection(true)
  }

  const removeVariant = (index: number) => {
    const updated = variants.filter((_, i) => i !== index)
    setVariants(updated)
    if (updated.length === 0) {
      setShowVariantsSection(false)
    }
  }

  const updateVariant = (index: number, field: keyof ProductVariant, value: any) => {
    const updated = [...variants]
    updated[index] = {
      ...updated[index],
      [field]: value
    }
    setVariants(updated)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!requireApprovedStore('editar productos')) return
    if (!activeStore || !productId) return

    try {
      setSaving(true)

      // Actualizar producto principal
      const productData = new FormData()
      productData.append('name', formData.name)
      productData.append('description', formData.description)
      productData.append('price', formData.price)
      productData.append('category_id', formData.category_id)
      productData.append('stock_quantity', formData.stock_quantity)
      productData.append('unit', formData.unit)
      productData.append('is_available', formData.is_available.toString())
      productData.append('is_featured', formData.is_featured.toString())
      // Enviar barcode siempre (puede ser string vacío para limpiarlo)
      productData.append('barcode', formData.barcode || '')

      // Si hay una imagen nueva seleccionada, subirla
      if (selectedImage) {
        productData.append('image', selectedImage)
      } else if (selectedImageKey) {
        // Si se seleccionó una imagen existente, usar su key
        productData.append('imageUrl', selectedImageKey)
      }

      await api.put(`/products/${productId}`, productData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      // Actualizar variantes
      if (variants.length > 0) {
        await Promise.all(
          variants.map(async (variant) => {
            let imageUrl = variant.imageUrl || ''
            
            // Si hay un archivo de imagen nuevo, subirlo primero
            if (variant.imageFile) {
              const variantImageData = new FormData()
              variantImageData.append('image', variant.imageFile)
              variantImageData.append('folder', 'products')
              
              try {
                const uploadResponse = await api.post('/upload/image', variantImageData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                })
                if (uploadResponse.data.success && uploadResponse.data.data?.key) {
                  imageUrl = uploadResponse.data.data.key
                  console.log(`Imagen subida para variante ${variant.variantName}:`, imageUrl)
                } else {
                  console.error('Respuesta de subida de imagen inválida:', uploadResponse.data)
                  toast.error(`Error al subir imagen de variante ${variant.variantName}`)
                  return // Saltar esta variante si falla la subida
                }
              } catch (error) {
                console.error('Error uploading variant image:', error)
                toast.error(`Error al subir imagen de variante ${variant.variantName}`)
                return // Saltar esta variante si falla la subida
              }
            } else if (variant.imageUrl && variant.imageUrl.trim() !== '') {
              // Si se seleccionó una imagen existente, usar su key directamente
              imageUrl = variant.imageUrl
            }
            
            console.log(`Guardando variante ${variant.variantName} con imageUrl:`, imageUrl)
            
            // Si la variante tiene ID, actualizarla; si no, crearla
            if (variant.id) {
              try {
                const response = await api.put(`/products/${productId}/variants/${variant.id}`, {
                  variantName: variant.variantName,
                  variantType: variant.variantType,
                  stockQuantity: variant.stockQuantity,
                  sku: variant.sku || '',
                  imageUrl: imageUrl || null // Enviar null si está vacío
                })
                console.log(`Variante ${variant.variantName} actualizada:`, response.data)
              } catch (error: any) {
                console.error(`Error updating variant ${variant.variantName}:`, error)
                console.error('Error response:', error.response?.data)
                toast.error(`Error al actualizar variante ${variant.variantName}`)
              }
            } else {
              try {
                const response = await api.post(`/products/${productId}/variants`, {
                  variantName: variant.variantName,
                  variantType: variant.variantType,
                  stockQuantity: variant.stockQuantity,
                  sku: variant.sku || '',
                  imageUrl: imageUrl || null // Enviar null si está vacío
                })
                console.log(`Variante ${variant.variantName} creada:`, response.data)
              } catch (error: any) {
                console.error(`Error creating variant ${variant.variantName}:`, error)
                console.error('Error response:', error.response?.data)
                toast.error(`Error al crear variante ${variant.variantName}`)
              }
            }
          })
        )
      }

      toast.success('Producto actualizado exitosamente')
      navigate(`/store/${activeStore.id}/products`)
    } catch (error: any) {
      console.error('Error updating product:', error)
      toast.error('Error al actualizar el producto')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando producto...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Header - Modern iMac Style */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-2xl border-b border-gray-200/60 shadow-sm">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <button
              onClick={() => navigate(`/store/${activeStore?.id}/products`)}
              className="p-2 sm:p-2.5 -ml-2 sm:-ml-2.5 rounded-xl hover:bg-gray-100/60 transition-all duration-200 active:scale-95 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
            </button>
            <div className="flex-1 text-center px-2 sm:px-4 min-w-0">
              <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-gray-900 tracking-tight truncate">
                Editar Producto
              </h1>
            </div>
            <div className="w-8 sm:w-10"></div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 safe-area-bottom">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Image */}
          <div className="bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg border border-gray-200/80 p-6 sm:p-8 hover:shadow-xl transition-all duration-300">
            <label className="block text-sm font-semibold text-gray-900 mb-4">
              Imagen del Producto
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center shadow-sm flex-shrink-0">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="product-image"
                />
                <label
                  htmlFor="product-image"
                  className="inline-flex items-center justify-center px-4 py-2.5 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 cursor-pointer transition-all duration-200 text-sm font-medium border border-gray-200 active:scale-95 shadow-sm hover:shadow-md"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Subir nueva
                </label>
                <button
                  type="button"
                  onClick={() => setShowImageSelector(true)}
                  className="inline-flex items-center justify-center px-4 py-2.5 bg-primary-50 text-primary-700 rounded-xl hover:bg-primary-100 transition-all duration-200 text-sm font-medium border border-primary-200 active:scale-95 shadow-sm hover:shadow-md"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Seleccionar existente
                </button>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200/80 p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Información Básica</h2>
            
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                Nombre del Producto *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                Descripción
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md resize-none"
                placeholder="Descripción del producto..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                  Precio *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                  Categoría *
                </label>
                <select
                  required
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                >
                  <option value="">Seleccionar categoría...</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                  Stock
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                  Unidad
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                >
                  <option value="unidad">Unidad</option>
                  <option value="kg">Kilogramo</option>
                  <option value="lb">Libra</option>
                  <option value="l">Litro</option>
                  <option value="ml">Mililitro</option>
                  <option value="paquete">Paquete</option>
                  <option value="caja">Caja</option>
                </select>
              </div>
            </div>

            {/* Código de Barras */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                Código de Barras
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md font-mono"
                  placeholder="Ingresa el código de barras"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 pt-2 sm:pt-3 border-t border-gray-100">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_available}
                  onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                  className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                />
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-700">Producto disponible</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                />
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-700">Producto destacado</span>
              </label>
            </div>
          </div>

          {/* Variants Section */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200/80 p-4 sm:p-5 md:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Variantes del Producto</h2>
              <button
                type="button"
                onClick={addVariant}
                className="flex items-center px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg sm:rounded-xl hover:bg-primary-700 transition-all duration-200 active:scale-95 text-xs sm:text-sm font-medium shadow-md shadow-primary-600/20 hover:shadow-lg"
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                Agregar Variante
              </button>
            </div>

            {variants.length > 0 && (
              <div className="space-y-3 sm:space-y-4">
                {variants.map((variant, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs sm:text-sm font-semibold text-gray-900">Variante {index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors active:scale-95"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                          Nombre *
                        </label>
                        <input
                          type="text"
                          required
                          value={variant.variantName}
                          onChange={(e) => updateVariant(index, 'variantName', e.target.value)}
                          className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                          placeholder="Ej: Cola, Naranja, Azul"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                          Tipo *
                        </label>
                        <select
                          required
                          value={variant.variantType}
                          onChange={(e) => updateVariant(index, 'variantType', e.target.value as any)}
                          className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 cursor-pointer"
                        >
                          <option value="flavor">Sabor</option>
                          <option value="color">Color</option>
                          <option value="size">Tamaño</option>
                          <option value="other">Otro</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                          Stock *
                        </label>
                        <input
                          type="number"
                          required
                          min="0"
                          value={variant.stockQuantity}
                          onChange={(e) => updateVariant(index, 'stockQuantity', parseInt(e.target.value) || 0)}
                          className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                          SKU
                        </label>
                        <input
                          type="text"
                          value={variant.sku || ''}
                          onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                          className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                          placeholder="Opcional"
                        />
                      </div>
                    </div>

                    {/* Variant Image */}
                    <div>
                      <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                        Imagen de la Variante (Opcional)
                      </label>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center relative flex-shrink-0">
                          {variant.imagePreview && variant.imagePreview.trim() !== '' ? (
                            <img 
                              src={variant.imagePreview} 
                              alt={`Preview ${variant.variantName}`} 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.error('Error cargando imagen de variante:', variant.variantName, variant.imagePreview)
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : variant.imageUrl && variant.imageUrl.trim() !== '' ? (
                            <ProductImage
                              imageKey={variant.imageUrl}
                              className="w-full h-full object-cover"
                              alt={variant.variantName}
                            />
                          ) : (
                            <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) handleVariantImageSelect(index, file)
                            }}
                            className="hidden"
                            id={`variant-image-${index}`}
                          />
                          <label
                            htmlFor={`variant-image-${index}`}
                            className="inline-flex items-center justify-center px-2 sm:px-2.5 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 cursor-pointer transition-all duration-200 text-[10px] sm:text-xs font-medium border border-gray-200 active:scale-95"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Subir nueva
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setVariantImageSelectorIndex(index)
                              setShowImageSelector(true)
                            }}
                            className="inline-flex items-center justify-center px-2 sm:px-2.5 py-1.5 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-all duration-200 text-[10px] sm:text-xs font-medium border border-primary-200 active:scale-95"
                          >
                            <Search className="w-3 h-3 mr-1" />
                            Existente
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {variants.length === 0 && (
              <p className="text-xs sm:text-sm text-gray-500 text-center py-6 sm:py-8">
                No hay variantes. Haz clic en "Agregar Variante" para crear una.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:gap-4 pt-4 sm:pt-6 pb-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate(`/store/${activeStore?.id}/products`)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 active:scale-95 font-medium text-sm sm:text-base shadow-sm hover:shadow-md"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-primary-600 text-white rounded-lg sm:rounded-xl hover:bg-primary-700 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30 text-sm sm:text-base"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>


      {/* Image Selector Modal */}
      {showImageSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl sm:rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-gray-200/50">
            <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200">
              <div className="flex items-start sm:items-center justify-between gap-3 mb-3 sm:mb-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">Seleccionar Imagen Existente</h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">Busca y selecciona una imagen de tu galería</p>
                </div>
                <button
                  onClick={() => {
                    setShowImageSelector(false)
                    setImageSearchTerm('')
                  }}
                  className="p-2 sm:p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                </button>
              </div>
              <div>
                <div className="relative">
                  <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    value={imageSearchTerm}
                    onChange={(e) => setImageSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre de archivo..."
                    className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                  />
                </div>
              </div>
            </div>
            <div className="p-3 sm:p-4 md:p-6 overflow-y-auto flex-1 bg-gray-50/50">
              {loadingImages ? (
                <div className="flex items-center justify-center py-12 sm:py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : availableImages.length === 0 ? (
                <div className="text-center py-12 sm:py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm sm:text-base text-gray-500 font-medium">No se encontraron imágenes</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                  {availableImages.map((image) => (
                    <button
                      key={image.key}
                      type="button"
                      onClick={() => {
                        if (variantImageSelectorIndex !== null) {
                          handleSelectExistingVariantImage(image.key, image.signedUrl)
                        } else {
                          handleSelectExistingImage(image.key, image.signedUrl)
                        }
                      }}
                      className="group relative aspect-square bg-gray-100 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all duration-200 hover:shadow-lg hover:scale-105"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.key}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] sm:text-xs text-center px-2 font-medium">
                          {image.key.split('/').pop()}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditProduct

